// react
import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// components
import {
  Accordion,
  Container,
  Dialog,
  Icon,
  Item,
  Label,
} from '@/components/atoms';
import { Icons, validateIcon } from '@/components/atoms/Icon';
import {
  Action,
  CareProfileHeader,
  FloatingActions,
} from '@/components/molecules';

// theme
import { theme } from '@/theme';

// models
import { Section } from '@karehero/models';

/**
 * CareProfileRoot introduces the care plan.
 */
export const CareProfileRoot = ({
  className,
  children,
  backLabel = 'Back',
  nextLabel = 'Next',
  onPrintPdf,
  limit,
  progress,
  sections,
  title,
  name,
  toExit,
  toExitLabel = 'Back',
  onNext,
  toNext,
  toPrevious,
  isNextDisabled = false,
}: CareProfileRootProps) => {
  // state
  const [isSectionsOpen, setIsSectionsOpen] = useState(false);

  return (
    <>
      <Wrapper className={className}>
        <StyledCareProfileHeader
          limit={limit}
          progress={progress}
          toBack={toExit}
          toBackLabel={toExitLabel}
          onPrintPdf={onPrintPdf}
          name={name}
          title={title}
          onOpenSections={() => setIsSectionsOpen(true)}
        />
        <StyledContainer size='md'>{children}</StyledContainer>
      </Wrapper>
      <Dialog
        variant='sheet'
        isOpen={isSectionsOpen}
        setIsOpen={setIsSectionsOpen}
        direction='right'
      >
        <JumpToSectionTitle>Jump to a section</JumpToSectionTitle>
        <SectionWrapper>
          <Accordion
            items={sections?.map((section: Section) => {
              const colorAndIconMap = {
                complete: {
                  primary: theme.color.success[10],
                  secondary: theme.color.success[40],
                  icon: 'check',
                },
                inProgress: {
                  primary: theme.color.warning[10],
                  secondary: theme.color.warning[40],
                  icon: 'minus',
                },
                notStarted: {
                  primary: theme.color.neutral[10],
                  secondary: theme.color.neutral[40],
                  icon: 'check',
                },
              };

              const item: Item = {
                id: section.id,
                label: section.title,
                content: (
                  <>
                    {section.subSections?.map((subsec) => (
                      <SectionItem
                        key={subsec.id}
                        to={subsec.to ? subsec.to : ''}
                        isActive={location.pathname === subsec.to}
                      >
                        <StyledIconContainer isCompleted={subsec.sectionStatus}>
                          <Icon
                            icon={
                              colorAndIconMap[subsec.sectionStatus]
                                .icon as Icons
                            }
                          />
                        </StyledIconContainer>
                        <StyedLabel id={subsec.id} text={subsec.title} />
                      </SectionItem>
                    ))}
                  </>
                ),
                icon: validateIcon(colorAndIconMap[section.sectionStatus].icon),
                primaryColor: colorAndIconMap[section.sectionStatus].primary,
                secondaryColor:
                  colorAndIconMap[section.sectionStatus].secondary,
              };
              return item;
            })}
          />
        </SectionWrapper>
      </Dialog>
      <FloatingActions
        isSticky
        direction={toPrevious ? 'row' : 'col'}
        actions={[
          ...(toPrevious
            ? [
                {
                  label: backLabel,
                  variant: 'secondary',
                  to: toPrevious,
                } as Action,
              ]
            : []),
          {
            label: nextLabel,
            variant: 'primary',
            onPress: onNext,
            to: toNext,
            iconRight: 'chevron-right',
            isDisabled: isNextDisabled === true,
          },
        ]}
      />
    </>
  );
};

const StyledIconContainer = styled.div<{ isCompleted: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.theme.spacing[4]};
  border-radius: 99999px;
  width: 24px;
  min-width: 24px;
  max-width: 24px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
  background-color: ${(props) =>
    props.isCompleted === 'complete'
      ? props.theme.color.success[40]
      : props.isCompleted === 'inProgress'
        ? props.theme.color.warning[40]
        : props.theme.color.neutral[40]};
  color: white;
  margin-right: ${(props) => props.theme.spacing[8]};
`;

const Wrapper = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const StyledContainer = styled(Container)`
  flex: 1;
  overflow-y: auto;
`;

const StyledCareProfileHeader = styled(CareProfileHeader)`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const JumpToSectionTitle = styled.div`
  font: ${(props) => props.theme.typography.body.md.semibold};
  color: ${(props) => props.theme.color.neutral[80]};
  margin-bottom: ${(props) => props.theme.spacing[16]};
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  overflow-y: auto;
  max-height: 100%;
`;

const SectionItem = styled(Link)<{ isActive: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 4px;
  margin-left: ${(props) => props.theme.spacing[16]};
  padding-left: ${(props) => props.theme.spacing[16]};
  transition-duration: 0.2s;
  background: ${(props) =>
    props.isActive ? props.theme.color.neutral[30] : 'transparent'};
  &:hover {
    background: ${(props) => props.theme.color.neutral[30]};
  }
`;

const StyedLabel = styled(Label)`
  cursor: pointer;
  padding: ${(props) => props.theme.spacing[12]} 0;
`;

interface CareProfileRootProps {
  className?: string;
  children: React.ReactNode;
  nextLabel?: string;
  backLabel?: string;
  title?: string;
  onPrintPdf?: () => void;
  limit?: number;
  progress?: number;
  sections?: Section[];
  name?: string;
  toExit?: string;
  toExitLabel?: string;
  onNext?: () => void;
  toNext?: string;
  toPrevious?: string;
  isNextDisabled?: boolean;
}

CareProfileRoot.displayName = 'CareProfileRoot';

export default CareProfileRoot;
